/**
 * Demo data for the Live Message Feed's driver universe and dispatch "boards".
 *
 * The Western Express continuing-coverage feed watches a very large fleet, so
 * this module fabricates a few hundred synthetic drivers (layered on top of the
 * ten hand-authored detailed drivers from `data.ts`), a stream of mocked
 * messages across them, and a few dozen named boards. A board is a net-new
 * driver-group concept distinct from the `division` field and the
 * `FreightStatusBoard` table; membership partitions the fleet — each driver
 * belongs to exactly one board — and is intended to later map onto Platform
 * Science groups. Names and membership are fake demo data.
 *
 * Everything here is deterministic (seeded hashes), so reloading the prototype
 * yields a stable universe — boards keep their sizes, drivers keep their
 * boards, and truck numbers stay put across a driver's messages.
 */

import { assignedByFor } from "./assignments";
import { DRIVERS, TASKS } from "./data";
import { hash } from "./hash";
import type { Board, DispatchTask, Driver, DriverActivityEvent, EmptyCallFailure, TaskState } from "./types";

export interface FeedDriver {
  id: string;
  name: string;
  /** Truck number — the feed's primary identifier. */
  truck: string;
  /** True for the ten hand-authored drivers that have full Active Context. */
  detailed: boolean;
  /** Coarse driving status shown under the driver name in a feed row. */
  drivingStatus: string;
}

export interface FeedMessage {
  itemId: string;
  driverId: string;
  /** ISO timestamp. */
  createdAt: string;
  preview: string;
  direction: "inbound" | "outbound";
  unread: boolean;
  isSilent: boolean;
  isFromBot: boolean;
  hasTranscription: boolean;
  /** System alert: the driver's preplan failed to dispatch (needs dispatcher action). */
  emptyCallFailed?: boolean;
  /** This message was handed off to the current dispatcher by another manager. */
  assignedToMe?: boolean;
  /** Who assigned it (when `assignedToMe`). */
  assignedBy?: string;
}

/** Task states that count as "un-addressed" for board badges. */
const UNADDRESSED_STATES: ReadonlySet<TaskState> = new Set(["suggested", "requires_attention"]);

export function isUnaddressed(state: TaskState): boolean {
  return UNADDRESSED_STATES.has(state);
}

function pick<T>(arr: T[], seed: string): T {
  return arr[hash(seed) % arr.length];
}

/** Deterministic 5-digit truck number for a synthetic driver. */
function syntheticTruck(driverId: string): string {
  return String(10000 + (hash(`truck::${driverId}`) % 90000));
}

export const FIRST_NAMES = [
  "James",
  "Maria",
  "Robert",
  "Linda",
  "Michael",
  "Patricia",
  "David",
  "Jennifer",
  "Carlos",
  "Aisha",
  "Tyrone",
  "Mei",
  "Sergei",
  "Fatima",
  "Hank",
  "Rosa",
  "Dwayne",
  "Nadia",
  "Cole",
  "Yusuf",
  "Earl",
  "Bianca",
  "Andre",
  "Latoya",
  "Wes",
  "Sam",
  "Diego",
  "Tanya",
  "Priya",
  "Marcus",
  "Hector",
  "Gloria",
  "Travis",
  "Renee",
  "Omar",
  "Dana",
  "Curtis",
  "Selena",
  "Brett",
  "Imani",
];

export const LAST_NAMES = [
  "Bell",
  "Martinez",
  "Riley",
  "Nair",
  "Thompson",
  "Grant",
  "Mendez",
  "Wu",
  "Olsen",
  "Flores",
  "Ramirez",
  "Brooks",
  "Jennings",
  "Whitfield",
  "Vega",
  "Foster",
  "Delgado",
  "Simmons",
  "Carmichael",
  "Ortiz",
  "Okafor",
  "Coleman",
  "Nguyen",
  "Petrov",
  "Haddad",
  "Boone",
  "Sokolov",
  "Greer",
  "Mathis",
  "Underwood",
  "Pruitt",
  "Salazar",
  "Dunlap",
  "Beasley",
  "Maddox",
  "Crews",
  "Vaughn",
  "Ackerman",
  "Holloway",
  "Reyes",
];

const DRIVING_STATUSES = ["driving", "on_duty", "on_duty", "sleeper", "off_duty", "driving"];

const SYNTHETIC_DRIVER_COUNT = 460;

/** Map the ten detailed drivers into the feed shape. */
const DETAILED_FEED_DRIVERS: FeedDriver[] = DRIVERS.map((d) => ({
  id: d.id,
  name: d.name,
  truck: d.truck,
  detailed: true,
  drivingStatus: d.velocityMph === 0 ? "on_duty" : "driving",
}));

const SYNTHETIC_FEED_DRIVERS: FeedDriver[] = Array.from({ length: SYNTHETIC_DRIVER_COUNT }, (_, i) => {
  const id = `feed_drv_${i}`;
  const first = pick(FIRST_NAMES, `first::${id}`);
  const last = pick(LAST_NAMES, `last::${id}`);
  return {
    id,
    name: `${first} ${last}`,
    truck: syntheticTruck(id),
    detailed: false,
    drivingStatus: pick(DRIVING_STATUSES, `status::${id}`),
  };
});

export const FEED_DRIVERS: FeedDriver[] = [...DETAILED_FEED_DRIVERS, ...SYNTHETIC_FEED_DRIVERS];

export const FEED_DRIVER_BY_ID: ReadonlyMap<string, FeedDriver> = new Map(FEED_DRIVERS.map((d) => [d.id, d]));

const DETAILED_DRIVER_IDS = DETAILED_FEED_DRIVERS.map((d) => d.id);

// --- Boards ---------------------------------------------------------------

/** The four named boards called out in the ticket, shown first. */
const NAMED_BOARDS: { name: string; description: string }[] = [
  { name: "HANSA", description: "Hanover-Savannah lane coverage" },
  { name: "FLP", description: "Florida panhandle dedicated" },
  { name: "VFONT", description: "Valley front-haul reefer" },
  { name: "FNASH", description: "Nashville feeder network" },
];

/** Ids of the four ticket-named boards, kept distinct so the picker can pin them first. */
export const NAMED_BOARD_IDS: ReadonlySet<string> = new Set(NAMED_BOARDS.map((m) => `board_${m.name.toLowerCase()}`));

const BOARD_PREFIXES = [
  "HAN",
  "FLP",
  "VF",
  "FN",
  "TX",
  "MID",
  "PAC",
  "GLF",
  "ATL",
  "NE",
  "SW",
  "RKY",
  "OH",
  "CAR",
  "DLV",
];
const BOARD_SUFFIXES = ["SA", "ONT", "ASH", "EX", "CO", "RG", "DC", "HUB", "LN", "OPS", "1", "2", "3", "N", "S"];

const TOTAL_BOARDS = 28;

/**
 * Build the boards as a strict partition of the fleet: every driver belongs to
 * exactly one board. The ten detailed drivers are spread round-robin across the
 * four named boards (so their backlog dominates those boards and resolving a
 * task visibly moves the count); synthetic drivers are dealt to one board each
 * by a deterministic weighted draw, so board sizes vary naturally (~10–35).
 */
function buildBoards(): Board[] {
  const defs: { id: string; name: string; description?: string; weight: number }[] = [];
  const usedNames = new Set<string>();

  for (const meta of NAMED_BOARDS) {
    usedNames.add(meta.name);
    // Named boards get the lowest weight so they stay small and detailed-driver heavy.
    defs.push({ id: `board_${meta.name.toLowerCase()}`, name: meta.name, description: meta.description, weight: 1 });
  }
  for (let i = 0; i < TOTAL_BOARDS - NAMED_BOARDS.length; i++) {
    const id = `board_gen_${i}`;
    // Keep board names unique so the picker has no ambiguous duplicates.
    let name = `${pick(BOARD_PREFIXES, `bp::${id}`)}${pick(BOARD_SUFFIXES, `bs::${id}`)}`;
    if (usedNames.has(name)) name = `${name}-${i}`;
    usedNames.add(name);
    defs.push({ id, name, weight: 1 + (hash(`weight::${id}`) % 4) });
  }

  const membersByBoardId = new Map<string, string[]>(defs.map((d) => [d.id, []]));

  DETAILED_DRIVER_IDS.forEach((driverId, i) => {
    membersByBoardId.get(defs[i % NAMED_BOARDS.length].id)?.push(driverId);
  });

  const totalWeight = defs.reduce((sum, d) => sum + d.weight, 0);
  for (const driver of SYNTHETIC_FEED_DRIVERS) {
    let roll = hash(`board::${driver.id}`) % totalWeight;
    const def = defs.find((d) => (roll -= d.weight) < 0) ?? defs[defs.length - 1];
    membersByBoardId.get(def.id)?.push(driver.id);
  }

  return defs.map((d) => ({
    id: d.id,
    name: d.name,
    description: d.description,
    driverIds: membersByBoardId.get(d.id) ?? [],
  }));
}

export const BOARDS: Board[] = buildBoards();

/** Reverse lookup: membership is a partition, so each driver has exactly one board. */
const BOARD_BY_DRIVER_ID: ReadonlyMap<string, Board> = new Map(
  BOARDS.flatMap((board) => board.driverIds.map((driverId) => [driverId, board] as const)),
);

/** The single board a driver belongs to. */
export function boardForDriver(driverId: string): Board | undefined {
  return BOARD_BY_DRIVER_ID.get(driverId);
}

// --- Un-addressed task counts --------------------------------------------

/**
 * Synthetic per-driver un-addressed task count for feed drivers that don't have
 * a hand-authored task. Weighted so most drivers have none and a minority carry
 * one to three open tasks — enough to make board badges read like real backlogs.
 */
function syntheticUnaddressedCount(driverId: string): number {
  const r = hash(`unaddressed::${driverId}`) % 100;
  if (r < 88) return 0;
  if (r < 97) return 1;
  return 2;
}

/**
 * Count un-addressed tasks for a single feed driver. Detailed drivers reflect
 * the live `DispatchTask` state (so resolving a task in the Task Feed moves the
 * count); synthetic drivers use their deterministic count.
 *
 * @param driverId - Feed driver id.
 * @param detailedTaskStateByDriverId - Current state of each detailed driver's task.
 */
function unaddressedForDriver(driverId: string, detailedTaskStateByDriverId: Map<string, TaskState[]>): number {
  const detailed = detailedTaskStateByDriverId.get(driverId);
  if (detailed) return detailed.filter(isUnaddressed).length;
  return syntheticUnaddressedCount(driverId);
}

/**
 * Total un-addressed tasks across every driver on a board.
 *
 * @param board - The board to count.
 * @param detailedTaskStateByDriverId - Live state of detailed drivers' tasks.
 */
export function boardUnaddressedCount(board: Board, detailedTaskStateByDriverId: Map<string, TaskState[]>): number {
  let total = 0;
  for (const driverId of board.driverIds) {
    total += unaddressedForDriver(driverId, detailedTaskStateByDriverId);
  }
  return total;
}

/**
 * Group detailed-driver task states by driver id, applying live resolution
 * overrides. Used to feed {@link boardUnaddressedCount}.
 *
 * @param resolved - Map of task id → overridden state (from dispatcher actions).
 */
export function detailedTaskStates(resolved: Record<string, TaskState>): Map<string, TaskState[]> {
  const byDriver = new Map<string, TaskState[]>();
  for (const task of TASKS) {
    const state = resolved[task.id] ?? task.state;
    const list = byDriver.get(task.driverId) ?? [];
    list.push(state);
    byDriver.set(task.driverId, list);
  }
  return byDriver;
}

// --- Message stream -------------------------------------------------------

const INBOUND_TEXTS = [
  "Stuck at the dock, they say my appointment isn't in the system.",
  "Running about 30 late, heavy traffic on the interstate.",
  "Empty and ready for my next load, where am I headed?",
  "Fuel light just came on, nearest stop is 40 miles out.",
  "Receiver won't take the load until 2, want me to wait or reload?",
  "Got the bills signed, pulling off the dock now.",
  "Truck won't start after my break, think it's the batteries.",
  "DOT scale ahead has me pulled in for inspection.",
  "Detention is past two hours now, still no dock door.",
  "Made the delivery early, all 26 pallets accepted.",
  "Weather is getting bad up here, visibility is dropping.",
  "Can you confirm the trailer number on this pre-plan?",
  "I'm at the shipper but the load isn't ready yet.",
  "Need a reset, I'm almost out of hours for today.",
  "Accident blocking two lanes ahead, traffic is stopped.",
  "Dropped the empty, hooked to the loaded trailer.",
  "What's my ETA showing on your end? GPS is acting up.",
  "Lumper wants cash, I don't have a comcheck.",
  "All good here, just rolling, will check in at the next stop.",
  "They short-loaded me, count is off by 3 pallets.",
];

const OUTBOUND_TEXTS = [
  "Copy, I'll notify the receiver of the delay.",
  "Your next load is being built, details in 20 minutes.",
  "Go ahead and take your 10-hour break at the next safe stop.",
  "Confirmed, trailer 5841 for the pre-plan.",
  "Sending a road call for the breakdown now, sit tight.",
  "Reschedule is in, new appointment is 14:00 tomorrow.",
  "Thanks for the update, keep me posted on the inspection.",
  "Take the reload out of Memphis, paperwork is in the app.",
  "I've logged the detention, we'll bill the customer.",
  "Drive safe through the weather, no rush on this one.",
];

/**
 * Whether a driver's preplan failed to dispatch, and why. Deterministic so the
 * live-feed alert and the Active Context indication agree.
 *
 * @param driverId - Feed driver id.
 */
export function emptyCallFailureFor(driverId: string): EmptyCallFailure | null {
  if (hash(`ecfail::${driverId}`) % 100 >= 14) return null;
  switch (hash(`ecreason::${driverId}`) % 3) {
    case 0:
      return { kind: "trailer", reason: "Invalid Trailer Number entered" };
    case 1:
      return { kind: "trailer", reason: "Trailer number not found in fleet" };
    default: {
      const required = 240 + (hash(`ecdeadhead::${driverId}`) % 13) * 10;
      return { kind: "mileage", reason: `Deadhead miles limit. Required: ${required}, Limit: 225` };
    }
  }
}

function makeEmptyCallFailureMessage(driverId: string, reason: string, createdMs: number): FeedMessage {
  return {
    itemId: `ecf_${hash(`${driverId}::${createdMs}`).toString(36)}`,
    driverId,
    createdAt: new Date(createdMs).toISOString(),
    preview: `*** Preplan Failed to Dispatch *** Reason: ${reason}`,
    direction: "inbound",
    unread: true,
    isSilent: false,
    isFromBot: true,
    hasTranscription: false,
    emptyCallFailed: true,
  };
}

const MESSAGE_COUNT = 700;
const MESSAGE_WINDOW_HOURS = 6;
const EMPTY_CALL_FAILURE_MESSAGE_CAP = 30;

/**
 * Build the initial message stream: a few guaranteed messages per detailed
 * driver plus a broad spread across the synthetic universe, newest-first.
 *
 * @param nowMs - Reference "now" used to space timestamps over the window.
 */
export function buildInitialMessages(nowMs: number): FeedMessage[] {
  const messages: FeedMessage[] = [];
  const windowMs = MESSAGE_WINDOW_HOURS * 60 * 60 * 1000;

  // Guarantee every detailed driver a handful of recent messages so opening
  // their board (or pressing O on their row) always has traffic to show.
  DETAILED_FEED_DRIVERS.forEach((driver, di) => {
    for (let m = 0; m < 4; m++) {
      const seed = `detailmsg::${driver.id}::${m}`;
      messages.push(makeMessage(seed, driver.id, nowMs - (di * 90_000 + m * 240_000)));
    }
  });

  for (let i = 0; i < MESSAGE_COUNT; i++) {
    const seed = `msg::${i}`;
    const driver = SYNTHETIC_FEED_DRIVERS[hash(`driver::${seed}`) % SYNTHETIC_FEED_DRIVERS.length];
    const ageMs = hash(`age::${seed}`) % windowMs;
    messages.push(makeMessage(seed, driver.id, nowMs - ageMs));
  }

  // System integration alerts: one recent "Preplan Failed to Dispatch" per
  // driver that has a failure, capped so the stream isn't dominated by them.
  let failureCount = 0;
  for (const driver of FEED_DRIVERS) {
    const failure = emptyCallFailureFor(driver.id);
    if (!failure) continue;
    messages.push(
      makeEmptyCallFailureMessage(driver.id, failure.reason, nowMs - ((failureCount * 137_000) % windowMs)),
    );
    if (++failureCount >= EMPTY_CALL_FAILURE_MESSAGE_CAP) break;
  }

  return messages.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function makeMessage(seed: string, driverId: string, createdMs: number): FeedMessage {
  const inbound = hash(`dir::${seed}`) % 10 < 7; // ~70% inbound (driver traffic)
  const texts = inbound ? INBOUND_TEXTS : OUTBOUND_TEXTS;
  // A handful of inbound driver messages were handed off to the current dispatcher.
  const assignedToMe = inbound && hash(`assignme::${seed}`) % 100 < 5;
  return {
    itemId: `item_${hash(seed).toString(36)}_${seed.length}`,
    driverId,
    createdAt: new Date(createdMs).toISOString(),
    preview: pick(texts, `text::${seed}`),
    direction: inbound ? "inbound" : "outbound",
    unread: inbound && hash(`unread::${seed}`) % 3 === 0,
    isSilent: hash(`silent::${seed}`) % 40 === 0,
    isFromBot: !inbound && hash(`bot::${seed}`) % 12 === 0,
    hasTranscription: inbound,
    assignedToMe: assignedToMe || undefined,
    assignedBy: assignedToMe ? assignedByFor(driverId) : undefined,
  };
}

/**
 * Build one brand-new inbound message "arriving" at `nowMs`, used to simulate
 * the live stream while the feed isn't paused.
 *
 * @param nowMs - Arrival time of the new message.
 * @param seq - Monotonic counter so each generated message id is unique.
 */
export function makeLiveMessage(nowMs: number, seq: number): FeedMessage {
  const seed = `live::${nowMs}::${seq}`;
  const driver = FEED_DRIVERS[hash(`livedrv::${seed}`) % FEED_DRIVERS.length];
  return makeMessage(seed, driver.id, nowMs);
}

/**
 * The per-driver message-history window. The thread and the activity timeline are
 * both spread across this span (ending ~now) so they interleave convincingly.
 */
const HISTORY_WINDOW_MS = 5 * 60 * 60 * 1000;

/** Wall-clock "HH:MM" for a timestamp, used by the activity timeline. */
function clockHHMM(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Build a per-driver message thread (oldest-first) for the driver message view.
 * Messages are spread across the history window so driver activity can fall
 * between them rather than clustering at one end.
 *
 * @param driverId - The feed driver whose history to synthesize.
 */
export function buildDriverThread(driverId: string): FeedMessage[] {
  const count = 12 + (hash(`threadlen::${driverId}`) % 7);
  const nowMs = Date.now();
  const messages: FeedMessage[] = [];
  for (let m = 0; m < count; m++) {
    const seed = `thread::${driverId}::${m}`;
    const base = nowMs - HISTORY_WINDOW_MS + ((m + 0.5) / count) * HISTORY_WINDOW_MS;
    const jitter = ((hash(`threadjit::${seed}`) % 9) - 4) * 60 * 1000;
    messages.push(makeMessage(seed, driverId, Math.min(nowMs - 1000, base + jitter)));
  }
  return messages;
}

/** Tasks for a driver (detailed drivers only); used by the driver message view. */
export function tasksForDriver(driverId: string): DispatchTask[] {
  return TASKS.filter((t) => t.driverId === driverId);
}

/**
 * Full Active-Context payload for a feed driver. The ten hand-authored drivers
 * return their real record; synthetic feed drivers borrow a deterministic
 * template (overriding id/name/truck) so every feed row can open a plausible
 * context panel.
 *
 * @param driverId - Feed driver id.
 */
export function buildFeedDriverContext(driverId: string): Driver {
  const emptyCallFailure = emptyCallFailureFor(driverId) ?? undefined;
  const detailed = DRIVERS.find((d) => d.id === driverId);
  if (detailed) return { ...detailed, emptyCallFailure };
  const feed = FEED_DRIVER_BY_ID.get(driverId);
  const template = DRIVERS[hash(`ctx::${driverId}`) % DRIVERS.length];
  return {
    ...template,
    id: driverId,
    name: feed?.name ?? template.name,
    truck: feed?.truck ?? template.truck,
    emptyCallFailure,
  };
}

const ACTIVITY_CITIES = [
  "Memphis, TN",
  "Dallas, TX",
  "Atlanta, GA",
  "Indianapolis, IN",
  "Phoenix, AZ",
  "Nashville, TN",
  "Kansas City, MO",
  "Columbus, OH",
  "Denver, CO",
  "Charlotte, NC",
];

const ACTIVITY_CUSTOMERS = ["Kroger DC", "Walmart DC", "Target DC", "Amazon FC", "Home Depot RDC", "Publix DC"];

const DETAILED_ACTIVITY_BY_ID: ReadonlyMap<string, DriverActivityEvent[]> = new Map(
  DRIVERS.filter((d) => d.activity).map((d) => [d.id, d.activity ?? []]),
);

/** A driver's activity events without timestamps; recent times are assigned later. */
function syntheticActivity(driverId: string): Omit<DriverActivityEvent, "timestamp">[] {
  const shipperCity = pick(ACTIVITY_CITIES, `actship::${driverId}`);
  const receiverCity = pick(ACTIVITY_CITIES, `actrecv::${driverId}`);
  const fuelCity = pick(ACTIVITY_CITIES, `actfuel::${driverId}`);
  const customer = pick(ACTIVITY_CUSTOMERS, `actcust::${driverId}`);
  const shipper = `${pick(ACTIVITY_CUSTOMERS, `actshipname::${driverId}`)} — ${shipperCity}`;
  const receiver = `${customer} — ${receiverCity}`;
  return [
    { id: `${driverId}_act_empty`, kind: "empty_call", location: shipper, detail: "Called empty" },
    {
      id: `${driverId}_act_loaded`,
      kind: "loaded_call",
      location: shipper,
      detail: `Loaded · trailer ${5000 + (hash(`acttrailer::${driverId}`) % 4000)}`,
    },
    { id: `${driverId}_act_depart`, kind: "departure", location: shipper },
    { id: `${driverId}_act_fuel`, kind: "arrival", location: `Pilot — ${fuelCity}`, detail: "Fuel & break" },
    { id: `${driverId}_act_fueldepart`, kind: "departure", location: `Pilot — ${fuelCity}` },
    { id: `${driverId}_act_arrive`, kind: "arrival", location: receiver },
    { id: `${driverId}_act_depart2`, kind: "departure", location: receiver },
  ];
}

/**
 * Resolve a driver's activity timeline, spread across the history window so the
 * empty/loaded calls, arrivals, and departures interleave with the message
 * thread instead of clustering. Detailed drivers reuse their authored event
 * content; synthetic feed drivers get a deterministic set.
 *
 * @param driverId - Feed driver id.
 */
export function buildDriverActivity(driverId: string): DriverActivityEvent[] {
  const base = DETAILED_ACTIVITY_BY_ID.get(driverId) ?? syntheticActivity(driverId);
  const nowMs = Date.now();
  return base.map((event, i) => {
    const at = nowMs - HISTORY_WINDOW_MS + ((i + 0.5) / base.length) * HISTORY_WINDOW_MS;
    const jitter = ((hash(`actjit::${driverId}::${i}`) % 7) - 3) * 60 * 1000;
    return { ...event, timestamp: clockHHMM(at + jitter) };
  });
}
